/**
 * Operations Calendar Component
 *
 * Main calendar component wrapping FullCalendar.
 */

import { useCallback, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import type { EventClickArg, EventDropArg, DatesSetArg } from '@fullcalendar/core';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, CalendarX } from 'lucide-react';
import { useCalendarEvents, useRescheduleEvent } from '../hooks';
import { renderEventContent } from './CalendarEventContent';
import { CalendarEventPopover } from './CalendarEventPopover';
import type { CalendarFilters, CalendarEvent, DateRange } from '../types';
import { toISODateString, getCurrentMonthStartInMontrealTz } from '../utils';
import { addMonths } from 'date-fns';

interface OperationsCalendarProps {
  filters: CalendarFilters;
}

export function OperationsCalendar({ filters }: OperationsCalendarProps) {
  const calendarRef = useRef<FullCalendar>(null);

  // Date range state - initialize with Montreal timezone
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // Start at the beginning of current month
    const start = getCurrentMonthStartInMontrealTz();
    // End at the beginning of the month after next (spans current + next month)
    const end = addMonths(start, 2);
    return { start, end };
  });

  // Popover state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  // Track in-flight reschedule to prevent race conditions
  const [isRescheduling, setIsRescheduling] = useState(false);

  // Build query params
  const queryParams = {
    start: toISODateString(dateRange.start),
    end: toISODateString(dateRange.end),
    types: filters.types.length < 3 ? filters.types.join(',') : undefined,
    projectTypes: filters.projectTypes.length < 3 ? filters.projectTypes.join(',') : undefined,
    assigneeIds: filters.assigneeIds.length > 0 ? filters.assigneeIds.join(',') : undefined,
    includeCompleted: filters.includeCompleted || undefined,
  };

  // Fetch events
  const { data, isLoading, isError, error } = useCalendarEvents(queryParams);

  // Reschedule mutation
  const reschedule = useRescheduleEvent();

  // Handle date range change from calendar navigation
  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    setDateRange({
      start: arg.start,
      end: arg.end,
    });
  }, []);

  // Handle event click
  const handleEventClick = useCallback((arg: EventClickArg) => {
    const event = arg.event;
    const extendedProps = event.extendedProps;

    // Validate required extended properties
    if (!extendedProps?.type || !extendedProps?.sourceId) {
      console.error('Event missing required extended properties:', {
        id: event.id,
        type: extendedProps?.type,
        sourceId: extendedProps?.sourceId,
      });
      toast.error('Unable to display event details');
      return;
    }

    const calendarEvent: CalendarEvent = {
      id: event.id,
      title: event.title,
      start: event.startStr,
      end: event.endStr || undefined,
      allDay: event.allDay,
      type: extendedProps.type,
      sourceId: extendedProps.sourceId,
      sourceType: extendedProps.sourceType,
      status: extendedProps.status,
      priority: extendedProps.priority,
      assigneeId: extendedProps.assigneeId,
      assigneeName: extendedProps.assigneeName,
      projectNumber: extendedProps.projectNumber,
      backgroundColor: event.backgroundColor,
      borderColor: event.borderColor,
      textColor: event.textColor,
      extendedProps: {
        accountName: extendedProps.accountName,
        description: extendedProps.description,
        milestoneKey: extendedProps.milestoneKey,
        progress: extendedProps.progress,
      },
    };

    setSelectedEvent(calendarEvent);
    setPopoverAnchor(arg.el);
    setPopoverOpen(true);
  }, []);

  // Handle event drop (drag-and-drop reschedule)
  const handleEventDrop = useCallback(
    async (arg: EventDropArg) => {
      // Prevent concurrent reschedules
      if (isRescheduling) {
        toast.warning('Please wait for the current operation to complete');
        arg.revert();
        return;
      }

      const event = arg.event;
      const eventType = event.extendedProps.type as string;

      // Milestones cannot be rescheduled independently
      if (eventType === 'milestone') {
        toast.error('Milestones cannot be rescheduled independently. Reschedule the project instead.');
        arg.revert();
        return;
      }

      const newDate = event.start;
      if (!newDate) {
        console.error('handleEventDrop: Event has no start date');
        arg.revert();
        return;
      }

      setIsRescheduling(true);
      const loadingToast = toast.loading(
        eventType === 'task' ? 'Updating task due date...' : 'Updating project scheduled date...'
      );

      try {
        const newDateStr = toISODateString(newDate);

        await reschedule.mutateAsync({
          id: event.id,
          type: eventType === 'task' ? 'task' : 'project',
          newStart: newDateStr,
        });

        toast.success(
          eventType === 'task'
            ? 'Task due date updated'
            : 'Project scheduled date updated',
          { id: loadingToast }
        );
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to reschedule event';
        toast.error(errorMessage, { id: loadingToast });
        arg.revert();
      } finally {
        setIsRescheduling(false);
      }
    },
    [reschedule, isRescheduling]
  );

  // Transform events for FullCalendar
  const events = data?.events.map((event) => ({
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end,
    allDay: event.allDay,
    backgroundColor: event.backgroundColor,
    borderColor: event.borderColor,
    textColor: event.textColor,
    editable: true, // Projects and tasks are draggable
    extendedProps: {
      type: event.type,
      sourceId: event.sourceId,
      sourceType: event.sourceType,
      status: event.status,
      priority: event.priority,
      assigneeId: event.assigneeId,
      assigneeName: event.assigneeName,
      projectNumber: event.projectNumber,
      accountName: event.extendedProps.accountName,
      description: event.extendedProps.description,
      milestoneKey: event.extendedProps.milestoneKey,
      progress: event.extendedProps.progress,
    },
  })) || [];

  // Show error toast but keep calendar visible if we have previous data
  if (isError) {
    if (!data) {
      // First load failed - show full error state
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading calendar</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load calendar events. Please try again.'}
          </AlertDescription>
        </Alert>
      );
    } else {
      // Subsequent fetch failed - show toast but keep calendar
      const errorMessage = error instanceof Error ? error.message : 'Failed to load calendar events';
      toast.error(errorMessage, {
        description: 'Showing previous data. Please try again.',
      });
    }
  }

  // Initial loading state (only when no data)
  if (isLoading && !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  // Empty state
  if (data && data.events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CalendarX className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">No events found</h3>
        <p className="text-sm text-muted-foreground mt-1">
          There are no scheduled projects or tasks in this date range.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="operations-calendar">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
          }}
          events={events}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          datesSet={handleDatesSet}
          editable={true}
          droppable={false}
          dayMaxEvents={4}
          height="auto"
          aspectRatio={1.8}
          nowIndicator={true}
          weekends={true}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5],
            startTime: '08:00',
            endTime: '18:00',
          }}
        />
      </div>

      <CalendarEventPopover
        event={selectedEvent}
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        anchorEl={popoverAnchor}
      />

      {/* Custom styles for FullCalendar */}
      <style>{`
        .operations-calendar .fc {
          font-family: inherit;
        }
        .operations-calendar .fc-toolbar-title {
          font-size: 1.25rem;
          font-weight: 600;
        }
        .operations-calendar .fc-button {
          font-size: 0.875rem;
          font-weight: 500;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
        }
        .operations-calendar .fc-button-primary {
          background-color: hsl(var(--primary));
          border-color: hsl(var(--primary));
        }
        .operations-calendar .fc-button-primary:not(:disabled):active,
        .operations-calendar .fc-button-primary:not(:disabled).fc-button-active {
          background-color: hsl(var(--primary));
          border-color: hsl(var(--primary));
        }
        .operations-calendar .fc-button-primary:disabled {
          background-color: hsl(var(--muted));
          border-color: hsl(var(--border));
        }
        .operations-calendar .fc-daygrid-day-number {
          font-size: 0.875rem;
          color: hsl(var(--foreground));
        }
        .operations-calendar .fc-col-header-cell-cushion {
          font-weight: 500;
          color: hsl(var(--muted-foreground));
        }
        .operations-calendar .fc-event {
          cursor: pointer;
          border-radius: 0.25rem;
          font-size: 0.75rem;
        }
        .operations-calendar .fc-event:hover {
          opacity: 0.9;
        }
        .operations-calendar .fc-daygrid-event {
          margin: 1px 2px;
        }
        .operations-calendar .fc-more-link {
          font-size: 0.75rem;
          color: hsl(var(--primary));
        }
        .operations-calendar .fc-timegrid-slot-label {
          font-size: 0.75rem;
        }
        .operations-calendar .fc-list-event-title {
          font-size: 0.875rem;
        }
        .operations-calendar .fc-list-day-cushion {
          background-color: hsl(var(--muted));
        }
      `}</style>
    </>
  );
}
